import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GlobalSearch from './GlobalSearch';
import { NotificationBell } from './NotificationBell';
import { MessagesIcon } from './MessagesIcon';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Trophy, Plus, User, LogOut, Shield, Store, Menu, X, ChevronDown,
  Users, Megaphone, Percent,
  History, Globe, Calendar, Search, Settings, BarChart3, CreditCard, Code2, UserCog,
  Check, Home, Smartphone, Inbox, TrendingUp, MessageCircle,
} from 'lucide-react';

const adminItems = [
  { path: '/admin/users', label: 'Utilisateurs', icon: Users },
  { path: '/admin/tournaments', label: 'Tous les tournois', icon: Trophy },
  { path: '/admin/notifications', label: 'Notifications', icon: Megaphone },
  { path: '/admin/commissions', label: 'Commissions', icon: Percent },
  { path: '/admin/activity-logs', label: 'Journal', icon: History },
  { path: '/admin/backoffice', label: 'Back Office', icon: Settings },
  { path: '/admin/shop-requests', label: 'Demandes PRO', icon: Inbox },
  { path: '/admin/shops', label: 'Boutiques', icon: Store },
];

export default function ShopLayout({ children }) {
  const { user, logout, isAdmin, hasShop, selectedShop, shops, selectShop, isAuthenticated, canAccessAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const approvedShops = shops.filter(s => s.status === 'approved');
  const isActive = (path) => location.pathname === path;
  const isInPath = (prefix) => location.pathname.startsWith(prefix);
  const handleLogout = () => { logout(); navigate('/login'); };

  // Determine if we have a shop context active.
  // Admins get the same event-creation/management options as shops.
  const shopMode = !!selectedShop || isAdmin;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-[#121212] border-b border-[#333]">
        <div className="max-w-[1400px] mx-auto px-4 h-14 flex items-center justify-between">
          {/* Left: Profile Selector + Logo */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Profile picture / shop selector (when authenticated) */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 p-1 rounded-full hover:bg-[#252525] transition-colors" data-testid="nav-profile-selector">
                    <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center overflow-hidden border-2 border-[#333]">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-[#FF6B35]" />
                      )}
                    </div>
                    {approvedShops.length > 0 && <ChevronDown className="w-3 h-3 text-[#666]" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#1a1a1a] border-[#333] w-64">
                  {/* Current user context — clic sur le nom = profil public (vu par les autres) */}
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                    <Link to={`/user/${user?.id}`} className="flex flex-col items-start px-3 py-2" data-testid="avatar-public-profile">
                      <span className="text-sm font-medium text-white">{user?.pseudo || user?.name}</span>
                      <span className="text-xs text-[#666]">{selectedShop ? `Boutique: ${selectedShop.name}` : 'Voir mon profil public'}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#333]" />

                  {/* Mon profil (personal account settings) */}
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                    <Link to="/profile" className="flex items-center gap-2" data-testid="avatar-mon-profil">
                      <Settings className="w-4 h-4" />
                      <span className="flex-1">Mon profil</span>
                    </Link>
                  </DropdownMenuItem>

                  {/* Player mode */}
                  <DropdownMenuItem
                    onClick={() => selectShop(null)}
                    className={`cursor-pointer flex items-center gap-2 ${!selectedShop ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'focus:bg-[#252525]'}`}
                  >
                    <User className="w-4 h-4" />
                    <span className="flex-1">Mon compte joueur</span>
                    {!selectedShop && <Check className="w-4 h-4 text-[#FF6B35]" />}
                  </DropdownMenuItem>

                  {/* Shop list */}
                  {approvedShops.length > 0 && (
                    <>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <div className="px-3 py-1.5">
                        <p className="text-[10px] text-[#666] uppercase font-semibold tracking-wide">Mes Boutiques</p>
                      </div>
                      {approvedShops.map((shop) => (
                        <DropdownMenuItem
                          key={shop.id}
                          onClick={() => selectShop(selectedShop?.id === shop.id ? null : shop)}
                          className={`cursor-pointer flex items-center gap-2 ${selectedShop?.id === shop.id ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'focus:bg-[#252525]'}`}
                        >
                          <Store className="w-4 h-4" />
                          <span className="flex-1 truncate">{shop.name}</span>
                          {selectedShop?.id === shop.id && <Check className="w-4 h-4 text-[#FF6B35]" />}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                        <Link to="/player/create-shop" className="flex items-center gap-2 text-[#FF6B35]" data-testid="add-new-shop-btn">
                          <Plus className="w-4 h-4" />
                          <span className="flex-1">Ajouter une nouvelle boutique</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Create shop link if no shops */}
                  {approvedShops.length === 0 && (
                    <>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                        <Link to="/player/create-shop" className="flex items-center gap-2">
                          <Store className="w-4 h-4" />Espace PRO
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {/* Logo */}
            <Link to="/all-tournaments" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
              <img src="/logo-complet-arcanapp.png" alt="Arcanapp" className="h-7" onError={(e) => { e.target.style.display = 'none'; }} />
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 mx-4">
            {isAuthenticated && (
              <>
                {/* Global search */}
                <div className="w-56 xl:w-64 mr-1"><GlobalSearch /></div>
                {/* Créer un évènement (boutique/admin uniquement) */}
                {canAccessAdmin && (
                <Link
                  to="/create-tournament"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-bold whitespace-nowrap transition-colors text-[#FF6B35] hover:bg-[#FF6B35]/15"
                  data-testid="nav-create-event"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} />
                  Créer un évènement
                </Link>
                )}

                {/* Home */}
                <Link
                  to="/home"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive('/home') || isActive('/discover')
                      ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                      : 'text-[#999] hover:text-white hover:bg-[#252525]'
                  }`}
                  data-testid="nav-home"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>

                {/* Mes événements */}
                {canAccessAdmin ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive('/my-tournaments') || isActive('/shop/tournaments')
                          ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                          : 'text-[#999] hover:text-white hover:bg-[#252525]'
                      }`}
                      data-testid="nav-my-events"
                    >
                      <Trophy className="w-4 h-4" />
                      Mes événements
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-[#1a1a1a] border-[#333] w-56">
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/shop/tournaments?tab=active" className="flex items-center gap-2" data-testid="menu-my-events">
                        <Store className="w-4 h-4 text-[#4ECDC4]" />
                        <div>
                          <p className="text-white text-sm font-medium">Mes évènements</p>
                          <p className="text-[#666] text-[10px]">{selectedShop ? selectedShop.name : 'Actifs, inscrits, en cours, à clôturer'}</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/shop/tournaments?tab=archives" className="flex items-center gap-2" data-testid="menu-archives">
                        <History className="w-4 h-4 text-[#999]" />
                        <div>
                          <p className="text-white text-sm font-medium">Mes archives</p>
                          <p className="text-[#666] text-[10px]">Tournois clôturés</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                ) : (
                  <Link
                    to="/my-tournaments"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive('/my-tournaments')
                        ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                        : 'text-[#999] hover:text-white hover:bg-[#252525]'
                    }`}
                    data-testid="nav-my-events"
                  >
                    <Trophy className="w-4 h-4" />
                    Mes événements
                  </Link>
                )}

                {/* Paramètres dropdown (boutique/admin uniquement) */}
                {canAccessAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive('/my-shop') || isActive('/iframe') || isActive('/roles') || isActive('/payment')
                          ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                          : 'text-[#999] hover:text-white hover:bg-[#252525]'
                      }`}
                      data-testid="nav-settings"
                    >
                      <Settings className="w-4 h-4" />
                      Paramètres
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-[#1a1a1a] border-[#333] w-64">
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/shop/stats" className="flex items-center gap-2" data-testid="param-statistiques">
                        <TrendingUp className="w-4 h-4 text-[#22C55E]" />
                        <div>
                          <p className="text-white text-sm font-medium">Statistiques</p>
                          <p className="text-[#666] text-[10px]">KPIs, revenus, évolution & graphiques</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/my-shop" className="flex items-center gap-2" data-testid="param-ma-boutique">
                        <Store className="w-4 h-4 text-[#FF6B35]" />
                        <div>
                          <p className="text-white text-sm font-medium">Ma boutique</p>
                          <p className="text-[#666] text-[10px]">Coordonnées, logo, photos, réseaux</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/iframe" className="flex items-center gap-2" data-testid="param-mon-calendrier">
                        <Calendar className="w-4 h-4 text-[#4ECDC4]" />
                        <div>
                          <p className="text-white text-sm font-medium">Mon calendrier</p>
                          <p className="text-[#666] text-[10px]">Layouts & code HTML iframe</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/roles" className="flex items-center gap-2" data-testid="param-creer-compte">
                        <UserCog className="w-4 h-4 text-[#7B68EE]" />
                        <div>
                          <p className="text-white text-sm font-medium">Créer un compte</p>
                          <p className="text-[#666] text-[10px]">Comptes & rôles de la boutique</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                      <Link to="/payment" className="flex items-center gap-2" data-testid="param-finances">
                        <CreditCard className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-white text-sm font-medium">Mes informations financières</p>
                          <p className="text-[#666] text-[10px]">RIB, Stripe, stats & exports</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}

                {/* 5. Admin (ADMIN only) */}
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                          isInPath('/admin')
                            ? 'bg-red-500/15 text-red-400'
                            : 'text-[#999] hover:text-white hover:bg-[#252525]'
                        }`}
                        data-testid="nav-admin"
                      >
                        <Shield className="w-4 h-4" />
                        Admin
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-[#1a1a1a] border-[#333] w-56 max-h-[400px] overflow-y-auto">
                      {adminItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem
                            key={item.path}
                            asChild
                            className={`cursor-pointer ${isActive(item.path) ? 'bg-red-500/10 text-red-400' : 'focus:bg-[#252525]'}`}
                          >
                            <Link to={item.path} className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />{item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* 6. App mobile */}
                <Link
                  to="/app-mobile"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive('/app-mobile')
                      ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                      : 'text-[#999] hover:text-white hover:bg-[#252525]'
                  }`}
                  data-testid="nav-app-mobile"
                >
                  <Smartphone className="w-4 h-4" />
                  App mobile
                </Link>
              </>
            )}
          </nav>

          {/* Right: Login button or nothing (profile already on the left) */}
          <div className="flex items-center gap-2 shrink-0">
            {isAuthenticated && <MessagesIcon />}
            {isAuthenticated && <NotificationBell />}
            {!isAuthenticated && (
              <Link to="/login" className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-sm font-semibold transition-colors" data-testid="nav-login-btn">
                Se connecter
              </Link>
            )}

            {isAuthenticated && (
              <button onClick={handleLogout} className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-[#999] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 rounded-md text-[#999] hover:text-white hover:bg-[#252525]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="nav-mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {mobileOpen && (
          <nav className="lg:hidden border-t border-[#333] bg-[#121212] px-4 py-2 space-y-1 max-h-[70vh] overflow-y-auto">
            {isAuthenticated && (
              <>
                <div className="pt-2 pb-1"><GlobalSearch /></div>
                {canAccessAdmin && (
                <Link to="/create-tournament" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-bold text-[#FF6B35] hover:bg-[#FF6B35]/15">
                  <Plus className="w-4 h-4" strokeWidth={3} />Créer un évènement
                </Link>
                )}
                <Link to="/home" onClick={() => setMobileOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive('/home') || isActive('/discover') ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-[#999] hover:text-white hover:bg-[#252525]'}`}>
                  <Home className="w-4 h-4" />Home
                </Link>

                <div className="border-t border-[#333] pt-2 mt-2">
                  <p className="text-xs text-[#666] uppercase px-3 pb-1 font-semibold tracking-wide">Mes événements</p>
                  {canAccessAdmin ? (
                    <>
                  <Link to="/shop/tournaments?tab=active" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <Store className="w-4 h-4 text-[#4ECDC4]" />Mes évènements
                  </Link>
                  <Link to="/shop/tournaments?tab=archives" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <History className="w-4 h-4 text-[#999]" />Mes archives
                  </Link>
                    </>
                  ) : (
                  <Link to="/my-tournaments" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <Trophy className="w-4 h-4 text-[#4ECDC4]" />Mes inscriptions
                  </Link>
                  )}
                </div>

                <div className="border-t border-[#333] pt-2 mt-2">
                  <p className="text-xs text-[#666] uppercase px-3 pb-1 font-semibold tracking-wide">Paramètres</p>
                  {canAccessAdmin && (
                    <>
                  <Link to="/my-shop" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <Store className="w-4 h-4 text-[#FF6B35]" />Ma boutique
                  </Link>
                  <Link to="/iframe" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <Calendar className="w-4 h-4 text-[#4ECDC4]" />Mon calendrier
                  </Link>
                  <Link to="/roles" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <UserCog className="w-4 h-4 text-[#7B68EE]" />Créer un compte
                  </Link>
                  <Link to="/payment" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <CreditCard className="w-4 h-4 text-green-400" />Mes informations financières
                  </Link>
                    </>
                  )}
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]">
                    <Settings className="w-4 h-4" />Mon profil
                  </Link>
                  <Link to="/messages" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#999] hover:text-white hover:bg-[#252525]" data-testid="mobile-nav-messages">
                    <MessageCircle className="w-4 h-4 text-[#4ECDC4]" />Messagerie
                  </Link>
                </div>

                {isAdmin && (
                  <div className="border-t border-[#333] pt-2 mt-2">
                    <p className="text-xs text-red-400/60 uppercase px-3 pb-1 font-semibold tracking-wide">Administration</p>
                    {adminItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive(item.path) ? 'bg-red-500/15 text-red-400' : 'text-[#999] hover:text-white hover:bg-[#252525]'}`}>
                          <Icon className="w-4 h-4" />{item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}

                <Link to="/app-mobile" onClick={() => setMobileOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive('/app-mobile') ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-[#999] hover:text-white hover:bg-[#252525]'}`}>
                  <Smartphone className="w-4 h-4" />App mobile
                </Link>

                {/* Shop selector mobile */}
                {approvedShops.length > 1 && (
                  <div className="border-t border-[#333] pt-2 mt-2">
                    <p className="text-xs text-[#666] uppercase px-3 pb-1 font-semibold tracking-wide">Boutiques</p>
                    {approvedShops.map((shop) => (
                      <button key={shop.id} onClick={() => { selectShop(selectedShop?.id === shop.id ? null : shop); setMobileOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${selectedShop?.id === shop.id ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'text-[#999] hover:text-white hover:bg-[#252525]'}`}>
                        <Store className="w-4 h-4" /><span className="flex-1 text-left truncate">{shop.name}</span>{selectedShop?.id === shop.id && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}

                <div className="border-t border-[#333] pt-2 mt-2">
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm font-medium text-red-400 hover:bg-red-400/10 transition-colors">
                    <LogOut className="w-4 h-4" />Déconnexion
                  </button>
                </div>
              </>
            )}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}
